import { AuthTokens, KeycloakConfig } from "../types";
import { clearTokens, saveTokens } from "../utils/tokenUtils";

interface DialogMessageReceivedArg {
  message: string;
  origin: string | undefined;
}

interface DialogEventReceivedArg {
  error: number;
}

export const getKeycloakConfig = (): KeycloakConfig => {
  const url = process.env.KEYCLOAK_URL;
  const realm = process.env.KEYCLOAK_REALM;
  const clientId = process.env.KEYCLOAK_CLIENT_ID;

  if (!url || !realm || !clientId) {
    throw new Error("Keycloak-Konfiguration fehlt. Bitte überprüfen Sie die Umgebungsvariablen.");
  }

  return { url, realm, clientId };
};

export const login = async (): Promise<AuthTokens> => {
  return new Promise((resolve, reject) => {
    const dialogUrl = `${window.location.origin}/auth-dialog.html`;

    Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 30 }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Dialog konnte nicht geöffnet werden: ${asyncResult.error.message}`));
        return;
      }

      const dialog = asyncResult.value;
      let handled = false;

      const safeClose = () => {
        try {
          dialog.close();
        } catch (e) {
          console.warn("close failed", e);
        }
      };

      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (arg: DialogMessageReceivedArg) => {
          if (handled) return;

          try {
            const message = JSON.parse(arg.message);
            if (message.type === "success" && message.tokens) {
              handled = true;

              const authTokens: AuthTokens = message.tokens;
              try {
                await saveTokens(authTokens);
              } catch (e) {
                console.warn("saveTokens failed", e);
              }

              safeClose();
              resolve(authTokens);
              return;
            } else if (message.type === "error") {
              handled = true;
              safeClose();
              reject(new Error(message.error || "Authentifizierung fehlgeschlagen"));
            }
          } catch (error) {
            handled = true;
            safeClose();
            reject(error);
          }
        }
      );

      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (arg: DialogEventReceivedArg) => {
          if (arg.error === 12006) {
            reject(new Error("Authentifizierung vom Benutzer abgebrochen"));
          }
        }
      );
    });
  });
};

export const logout = async (): Promise<void> => {
  return new Promise((resolve) => {
    const dialogUrl = `${window.location.origin}/logout-dialog.html`;

    Office.context.ui.displayDialogAsync(dialogUrl, { height: 40, width: 30 }, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error("Failed to open logout dialog:", asyncResult.error.message);
        clearTokens()
          .then(() => resolve())
          .catch(() => resolve());
        return;
      }

      const dialog = asyncResult.value;
      const safeClose = () => {
        setTimeout(() => {
          try {
            dialog.close();
          } catch (e) {
            console.warn("close failed", e);
          }
        }, 0);
      };

      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (arg: DialogMessageReceivedArg) => {
          try {
            const message = JSON.parse(arg.message);

            if (message.type === "logout-success") {
              await clearTokens();
              safeClose();
              resolve();
            } else if (message.type === "logout-error") {
              console.error("Server logout error:", message.error);
              await clearTokens();
              safeClose();
              resolve();
            }
          } catch (error) {
            console.error("Error handling logout message:", error);
            await clearTokens();
            safeClose();
            resolve();
          }
        }
      );

      dialog.addEventHandler(Office.EventType.DialogEventReceived, async () => {
        await clearTokens();
        resolve();
      });
    });
  });
};
